/**
 * PolicyLabPage — EPIC-13: Policy Document Lab.
 *
 * Route: /app/policy-lab
 *
 * Phase flow:
 *  upload → (background processing) → qa → roleplay → feedback
 *
 * L1 agents see a gated message; L2+ agents see the full UI.
 *
 * EPIC-13-01: PDF upload with size/type validation, progress polling
 * EPIC-13-02: Q&A chat interface with citations
 * EPIC-13-03: Roleplay pivot button (active after ≥1 Q&A exchange)
 * EPIC-13-04: Feedback report with accuracy score and per-turn analysis
 * EPIC-13-05: Access gate enforced at API; this page reinforces it in UI
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import type { AgentLevel } from '../types';
import styles from './PolicyLabPage.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LabPhase = 'upload' | 'processing' | 'qa' | 'roleplay' | 'feedback';
type LabStatus = 'pending' | 'processing' | 'ready' | 'error';

interface LabSession {
  id: string;
  phase: LabPhase;
  status: LabStatus;
  progress_pct: number;
  document_name: string | null;
  doc_summary: string | null;
  created_at: string;
}

interface Citation {
  page: number;
  section: string | null;
  excerpt: string;
}

interface QAExchange {
  id: string;
  question: string;
  answer: string;
  citations: Citation[];
  confidence: number;
}

interface FeedbackTurn {
  turn_index: number;
  agent_utterance: string;
  error_type: 'factual_error' | 'omission' | 'unsupported_claim' | null;
  severity: 'critical' | 'minor' | null;
  correct_answer: string | null;
  source_citation: Citation | null;
}

interface FeedbackSummary {
  total_turns: number;
  error_count: number;
  critical_error_count: number;
  accuracy_pct: number;
  overall_score: number | null;
}

interface FeedbackReport {
  status: 'ready';
  summary: FeedbackSummary;
  turns: FeedbackTurn[];
}

const MAX_FILE_MB = 50;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getToken(): string {
  return sessionStorage.getItem('access_token') ?? '';
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<{ ok: boolean; data?: T; error?: string; status: number }> {
  const res = await fetch(`/api/v1${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${getToken()}`,
      ...options.headers,
    },
  });
  const json = await res.json().catch(() => ({}));
  if (res.ok) {
    return { ok: true, data: (json as { data: T }).data ?? (json as T), status: res.status };
  }
  return { ok: false, error: (json as { detail?: string }).detail ?? 'Error', status: res.status };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function UploadZone({
  onFile,
}: {
  onFile: (file: File) => void;
}): JSX.Element {
  const { t } = useTranslation('training');
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    (file: File) => {
      setError(null);
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setError(t('policyLab.upload.errorType'));
        return;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        setError(t('policyLab.upload.errorSize', { maxMb: MAX_FILE_MB }));
        return;
      }
      onFile(file);
    },
    [onFile, t],
  );

  return (
    <div
      className={`${styles.dropZone} ${dragOver ? styles.dragOver : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
      }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      aria-label={t('policyLab.upload.dropZoneLabel')}
      onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
    >
      <span className={styles.dropIcon}>📄</span>
      <p className={styles.dropText}>{t('policyLab.upload.hint')}</p>
      <p className={styles.dropSubText}>{t('policyLab.upload.sizeNote', { maxMb: MAX_FILE_MB })}</p>
      {error && <p className={styles.dropError}>{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className={styles.hiddenInput}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
    </div>
  );
}

function ProcessingProgress({ session }: { session: LabSession }): JSX.Element {
  const { t } = useTranslation('training');
  const steps = [
    t('policyLab.progress.uploading'),
    t('policyLab.progress.extracting'),
    t('policyLab.progress.indexing'),
    t('policyLab.progress.ready'),
  ];
  const pct = session.progress_pct;
  const stepIdx = pct < 25 ? 0 : pct < 60 ? 1 : pct < 90 ? 2 : 3;

  return (
    <div className={styles.processingBox}>
      <p className={styles.processingTitle}>{t('policyLab.progress.title')}</p>
      <div className={styles.stepList}>
        {steps.map((step, i) => (
          <span
            key={step}
            className={`${styles.step} ${i < stepIdx ? styles.stepDone : ''} ${i === stepIdx ? styles.stepActive : ''}`}
          >
            {step}
          </span>
        ))}
      </div>
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function QAPanel({
  session,
  history,
  onAsk,
  onStartRoleplay,
}: {
  session: LabSession;
  history: QAExchange[];
  onAsk: (question: string) => Promise<void>;
  onStartRoleplay: () => void;
}): JSX.Element {
  const { t } = useTranslation('training');
  const [question, setQuestion] = useState('');
  const [isAsking, setIsAsking] = useState(false);
  const [expandedCitation, setExpandedCitation] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history.length]);

  const handleSend = async () => {
    if (!question.trim() || isAsking) return;
    setIsAsking(true);
    await onAsk(question.trim());
    setQuestion('');
    setIsAsking(false);
  };

  return (
    <div className={styles.qaPanel}>
      <div className={styles.qaHeader}>
        <span className={styles.qaDocName}>{session.document_name}</span>
        {session.doc_summary && (
          <p className={styles.qaSummary}>{session.doc_summary}</p>
        )}
        <button
          className={styles.roleplayBtn}
          disabled={history.length === 0}
          onClick={onStartRoleplay}
          aria-label={t('policyLab.qa.startRoleplay')}
        >
          {t('policyLab.qa.startRoleplay')}
        </button>
      </div>

      <div className={styles.qaThread}>
        {history.length === 0 && (
          <p className={styles.qaEmpty}>{t('policyLab.qa.emptyHint')}</p>
        )}
        {history.map((exchange) => (
          <div key={exchange.id} className={styles.exchange}>
            <div className={styles.questionBubble}>{exchange.question}</div>
            <div className={styles.answerBubble}>
              <p>{exchange.answer}</p>
              {exchange.citations.length > 0 && (
                <div className={styles.citations}>
                  {exchange.citations.map((c, i) => (
                    <button
                      key={i}
                      className={styles.citationChip}
                      onClick={() =>
                        setExpandedCitation(
                          expandedCitation === `${exchange.id}-${i}` ? null : `${exchange.id}-${i}`,
                        )
                      }
                    >
                      {t('policyLab.qa.page', { page: c.page })}
                      {c.section ? ` · ${c.section}` : ''}
                    </button>
                  ))}
                  {exchange.citations.map(
                    (c, i) =>
                      expandedCitation === `${exchange.id}-${i}` && (
                        <blockquote key={`excerpt-${i}`} className={styles.excerpt}>
                          {c.excerpt}
                        </blockquote>
                      ),
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className={styles.qaInput}>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('policyLab.qa.placeholder')}
          className={styles.qaInputField}
          onKeyDown={(e) => e.key === 'Enter' && void handleSend()}
          disabled={isAsking}
          aria-label={t('policyLab.qa.inputLabel')}
        />
        <button
          className={styles.sendBtn}
          onClick={() => void handleSend()}
          disabled={isAsking || !question.trim()}
        >
          {isAsking ? t('policyLab.qa.asking') : t('policyLab.qa.send')}
        </button>
      </div>
    </div>
  );
}

function FeedbackPanel({ report }: { report: FeedbackReport }): JSX.Element {
  const { t } = useTranslation('training');
  const [expandedTurn, setExpandedTurn] = useState<number | null>(null);
  const { summary, turns } = report;

  return (
    <div className={styles.feedbackPanel}>
      <h2 className={styles.feedbackTitle}>{t('policyLab.feedback.title')}</h2>
      <div className={styles.summaryRow}>
        <div className={styles.accuracyScore}>
          <span className={styles.accuracyPct}>{summary.accuracy_pct.toFixed(0)}%</span>
          <span className={styles.accuracyLabel}>{t('policyLab.feedback.accuracy')}</span>
        </div>
        <div className={styles.summaryStats}>
          <span>{t('policyLab.feedback.totalTurns', { count: summary.total_turns })}</span>
          <span>{t('policyLab.feedback.errors', { count: summary.error_count })}</span>
          <span>{t('policyLab.feedback.critical', { count: summary.critical_error_count })}</span>
        </div>
      </div>

      <div className={styles.turnList}>
        {turns.map((turn) => (
          <div
            key={turn.turn_index}
            className={`${styles.turnItem} ${turn.error_type ? styles.turnError : styles.turnOk}`}
            onClick={() =>
              setExpandedTurn(expandedTurn === turn.turn_index ? null : turn.turn_index)
            }
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === 'Enter' &&
              setExpandedTurn(expandedTurn === turn.turn_index ? null : turn.turn_index)
            }
          >
            <div className={styles.turnHeader}>
              <span className={styles.turnIdx}>#{turn.turn_index + 1}</span>
              <span className={styles.turnUtterance}>{turn.agent_utterance}</span>
              {turn.error_type && (
                <span
                  className={`${styles.errorBadge} ${turn.severity === 'critical' ? styles.critical : styles.minor}`}
                >
                  {t(`policyLab.feedback.errorType.${turn.error_type}`)}
                </span>
              )}
            </div>
            {expandedTurn === turn.turn_index && turn.error_type && (
              <div className={styles.turnDetail}>
                {turn.correct_answer && (
                  <p>
                    <strong>{t('policyLab.feedback.correctAnswer')}</strong> {turn.correct_answer}
                  </p>
                )}
                {turn.source_citation && (
                  <blockquote className={styles.excerpt}>
                    {t('policyLab.feedback.source', { page: turn.source_citation.page })}{' '}
                    {turn.source_citation.excerpt}
                  </blockquote>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const L1_INT = 1;
const LEVEL_INT: Record<string, number> = { L1: 1, L2: 2, L3: 3, L4: 4, L5: 5 };

export function PolicyLabPage(): JSX.Element {
  const { t } = useTranslation('training');
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();

  // Re-sync the live profile on mount so an admin-side level promotion unlocks
  // Policy Lab without requiring the agent to log out and back in.
  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  const agentLevel = (user?.agent_level ?? 'L1') as AgentLevel;
  const agentLevelInt = LEVEL_INT[agentLevel] ?? 1;

  const [session, setSession] = useState<LabSession | null>(null);
  const [qaHistory, setQaHistory] = useState<QAExchange[]>([]);
  const [feedback, setFeedback] = useState<FeedbackReport | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Poll session status while processing
  const pollSession = useCallback(
    (sessionId: string) => {
      if (pollRef.current) clearTimeout(pollRef.current);
      pollRef.current = setTimeout(async () => {
        const res = await apiFetch<LabSession>(`/policy-lab/sessions/${sessionId}`);
        if (res.ok && res.data) {
          setSession(res.data);
          if (res.data.phase === 'processing' || res.data.status === 'processing') {
            pollSession(sessionId);
          }
        }
      }, 3000);
    },
    [],
  );

  // Poll feedback while generating
  const pollFeedback = useCallback(
    (sessionId: string) => {
      if (pollRef.current) clearTimeout(pollRef.current);
      pollRef.current = setTimeout(async () => {
        const res = await apiFetch<FeedbackReport | { status: string; progress_pct: number }>(
          `/policy-lab/sessions/${sessionId}/feedback`,
        );
        if (res.status === 202) {
          pollFeedback(sessionId);
        } else if (res.ok && res.data && (res.data as FeedbackReport).status === 'ready') {
          setFeedback(res.data as FeedbackReport);
        }
      }, 5000);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const handleFile = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);
    const form = new FormData();
    form.append('file', file);
    const res = await apiFetch<LabSession>('/policy-lab/sessions', {
      method: 'POST',
      body: form,
    });
    setIsUploading(false);
    if (res.ok && res.data) {
      setSession(res.data);
      pollSession(res.data.id);
    } else {
      setUploadError(res.error ?? t('policyLab.upload.errorGeneric'));
    }
  };

  const handleAsk = async (question: string) => {
    if (!session) return;
    const res = await apiFetch<{ answer: string; citations: Citation[]; confidence: number }>(
      `/policy-lab/sessions/${session.id}/ask`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      },
    );
    if (res.ok && res.data) {
      setQaHistory((prev) => [
        ...prev,
        {
          id: `qa-${Date.now()}`,
          question,
          answer: res.data!.answer,
          citations: res.data!.citations,
          confidence: res.data!.confidence,
        },
      ]);
    }
  };

  const handleStartRoleplay = async () => {
    if (!session) return;
    const res = await apiFetch<{ roleplay_session_id: string; websocket_url: string }>(
      `/policy-lab/sessions/${session.id}/start-roleplay`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
    );
    if (res.ok && res.data) {
      setSession((prev) => (prev ? { ...prev, phase: 'roleplay' } : prev));
      // In production: navigate to session page with policy context
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // L1 access gate
  if (agentLevelInt <= L1_INT) {
    return (
      <main className={styles.page}>
        <div className={styles.gatedCard}>
          <div className={styles.gatedGlow} aria-hidden="true" />
          <div className={styles.gatedLockChip}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#0055a5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <span className={styles.gatedBadge}>{t('policyLab.gated.badge', 'Unlocks at Level 2')}</span>
          <h1 className={styles.gatedTitle}>{t('policyLab.title')}</h1>
          <p className={styles.gatedDesc}>
            {t('policyLab.gated.message', { minLevel: 'L2', currentLevel: agentLevel })}
          </p>

          {/* Level progress L1 → L2 */}
          <div className={styles.levelTrack}>
            <div className={`${styles.levelNode} ${styles.levelNodeCurrent}`}>
              <span className={styles.levelDot}>{agentLevel}</span>
              <span className={styles.levelCaption}>{t('policyLab.gated.youAreHere', 'You are here')}</span>
            </div>
            <div className={styles.levelConnector} />
            <div className={`${styles.levelNode} ${styles.levelNodeTarget}`}>
              <span className={styles.levelDot}>L2</span>
              <span className={styles.levelCaption}>{t('policyLab.gated.unlocks', 'Policy Lab')}</span>
            </div>
          </div>

          <button className={styles.gatedCta} type="button" onClick={() => navigate('/learn')}>
            {t('policyLab.gated.cta', 'Continue your Learning Path')}
            <span aria-hidden="true"> →</span>
          </button>
        </div>
      </main>
    );
  }

  const phase = session?.phase ?? 'upload';

  return (
    <main className={styles.page}>
      <header className={styles.pageHeader}>
        <div className={styles.pageHeaderIcon}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0055a5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 2h6v3l3.5 9.5a2 2 0 0 1-1.9 2.6H7.4a2 2 0 0 1-1.9-2.6L9 5z" />
            <path d="M9 2h6M7 13h10" />
          </svg>
        </div>
        <div>
          <h1 className={styles.title}>{t('policyLab.title')}</h1>
          <p className={styles.pageSubtitle}>{t('policyLab.subtitle', 'Upload a policy document, study it with AI, then practise a roleplay.')}</p>
        </div>
      </header>

      {/* UPLOAD phase */}
      {phase === 'upload' && (
        <>
          {isUploading ? (
            <p className={styles.uploading}>{t('policyLab.upload.uploading')}</p>
          ) : (
            <UploadZone onFile={(f) => void handleFile(f)} />
          )}
          {uploadError && <p className={styles.error}>{uploadError}</p>}
        </>
      )}

      {/* PROCESSING phase */}
      {phase === 'processing' && session && <ProcessingProgress session={session} />}

      {/* QA phase */}
      {phase === 'qa' && session && (
        <QAPanel
          session={session}
          history={qaHistory}
          onAsk={handleAsk}
          onStartRoleplay={() => void handleStartRoleplay()}
        />
      )}

      {/* ROLEPLAY phase — placeholder while roleplay session is active */}
      {phase === 'roleplay' && (
        <div className={styles.roleplayActive}>
          <p>{t('policyLab.roleplay.active')}</p>
        </div>
      )}

      {/* FEEDBACK phase */}
      {phase === 'feedback' && session && (
        <>
          {feedback ? (
            <FeedbackPanel report={feedback} />
          ) : (
            <p className={styles.feedbackLoading}>
              {t('policyLab.feedback.loading')}
            </p>
          )}
        </>
      )}
    </main>
  );
}
